import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { DefectiveIcon } from '@euler/app/tv/components/icons/defective-icons/DefectiveIcon';
import { NormalIcon } from '@euler/app/tv/components/icons/defective-icons/NormalIcon';
import { UrgentIcon } from '@euler/app/tv/components/icons/defective-icons/UrgentIcon';
import { WarningIcon } from '@euler/app/tv/components/icons/defective-icons/WarningIcon';
import { Center } from '@euler/components/Center';
import { CircularProgress } from '@euler/components/CircularProgress';
import { HStack } from '@euler/components/layout/Stack';
import { getDefectiveLevelText } from '@euler/functions/getDefectiveLevel';
import { getDefectiveLevelOfItemList } from '@euler/functions/getDefectiveLevelOfItemList';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  DefectiveLevel,
  DefectiveLevelList,
  defectiveLevelTokenMapToValueMap,
  DefectiveSummary,
  makeDefectiveSummary,
} from '@euler/model';
import { InspectionReport } from '@euler/model/report';
import { comparerDesc } from '@euler/utils';
import { arr2groupmap, sorted } from '@euler/utils/array';
import { FC, memo, ReactNode, useCallback, useEffect, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSharedValue, withTiming } from 'react-native-reanimated';

export type DefectiveChartItemPropsBase = {
  labelStyle?: StyleProp<TextStyle>;
  counterStyle?: StyleProp<TextStyle>;
  labelContainerStyle?: StyleProp<ViewStyle>;
  noLabel?: boolean;
  size?: number;
  strokeWidth?: number;
  strokeColor: string;
  trackColor?: string;
};

type CellProps = {
  value: number;
  count: number;
  defectiveLevel: DefectiveLevel;
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;
  labelIcon?: ReactNode;
  onPress?: (defectiveLevel: DefectiveLevel) => void;
} & DefectiveChartItemPropsBase;

const DefectiveIconMap: { [p in DefectiveLevel]: FC<IconProps> } = {
  [DefectiveLevel.Urgent]: UrgentIcon,
  [DefectiveLevel.Warning]: WarningIcon,
  [DefectiveLevel.Defective]: DefectiveIcon,
  [DefectiveLevel.Fine]: NormalIcon,
};

type DefectiveChartPropsBase = {
  style?: StyleProp<ViewStyle>;
  iconStyle?: StyleProp<ViewStyle>;
  showIcons?: boolean;
  iconSize?: number;
  itemStyle?: StyleProp<ViewStyle>;
  onCellPress?: (defectiveLevel: DefectiveLevel) => void;
} & Omit<DefectiveChartItemPropsBase, 'strokeColor' | 'trackColor'>;

const kDefectiveColor = {
  urgent: 'rgba(244, 79, 99, 1)',
  warning: 'rgba(255, 138, 0, 1)',
  defective: 'rgba(217, 176, 30, 1)',
  normal: '#23cb58',
};

const kLabelColor = 'rgba(255, 255, 255, 0.4)';
const kDisabledLabelColor = 'rgba(0, 0, 0, 0.05)';
const kCounterColor = 'rgba(255, 255, 255, 0.8)';
const kCircleBgColor = 'rgba(255, 255, 255, 0.2)';
const kIconColor = 'rgba(0, 0, 0, 0.6)';

export const DefectiveStatsView = memo(
  ({
    report,
    flowId,
    style,
    itemStyle,
    size,
    iconSize,
    counterStyle,
    iconStyle,
    showIcons,
    onCellPress,
    ...props
  }: {
    report: InspectionReport | undefined;
    flowId?: string;
    style?: StyleProp<ViewStyle>;
  } & DefectiveChartPropsBase) => {
    const summary = useMemo<DefectiveSummary>(() => {
      const res = makeDefectiveSummary();
      const reportItems = (report?.items ?? []).filter(
        item => item.flowId == null || flowId == null || item.flowId === flowId,
      );
      const groupMap = arr2groupmap(reportItems, x => x.maintenanceAdvice);
      const keys = [...groupMap.keys()];
      for (const key of keys) {
        if (!key) {
          res[DefectiveLevel.Fine]++;
        } else {
          const items = groupMap.get(key)!;
          const defectiveLevel = getDefectiveLevelOfItemList(items);
          res[defectiveLevel]++;
        }
      }
      return res;
    }, [flowId, report?.items]);

    const total = DefectiveLevelList.reduce((res, x) => summary[x] + res, 0);

    const colorMap = useMemo(
      () => defectiveLevelTokenMapToValueMap(kDefectiveColor),
      [],
    );

    return (
      <View
        css={`
          flex-direction: row;
          justify-content: flex-start;
          align-items: center;
        `}
        style={style}
      >
        {sorted(DefectiveLevelList, comparerDesc).map(defectiveLevel => (
          <Cell
            size={size}
            defectiveLevel={defectiveLevel}
            count={summary[defectiveLevel]}
            key={defectiveLevel}
            value={total === 0 ? 0 : summary[defectiveLevel] / total}
            style={itemStyle}
            onPress={onCellPress}
            counterStyle={[
              {
                color:
                  summary[defectiveLevel] > 0
                    ? colorMap[defectiveLevel]
                    : kCounterColor,
              },
              counterStyle,
            ]}
            trackColor={kCircleBgColor}
            strokeColor={colorMap[defectiveLevel]}
            labelIcon={
              showIcons ? (
                <IconView
                  defectiveLevel={defectiveLevel}
                  size={iconSize}
                  color={kIconColor}
                  style={iconStyle}
                />
              ) : undefined
            }
            {...props}
          />
        ))}
      </View>
    );
  },
);

const Cell = memo(({ style, onPress, ...props }: CellProps) => {
  const handlePress = useCallback(() => {
    onPress?.(props.defectiveLevel);
  }, [onPress, props.defectiveLevel]);

  if (onPress != null) {
    return (
      <TouchableOpacity
        css={`
          justify-content: center;
          align-items: center;
        `}
        style={style}
        onPress={handlePress}
      >
        <CellContent {...props} />
      </TouchableOpacity>
    );
  }

  return (
    <Center style={style}>
      <CellContent {...props} />
    </Center>
  );
});

const CellContent = memo(
  ({
    size,
    defectiveLevel,
    noLabel,
    count,
    value,
    strokeWidth,
    labelStyle,
    labelContainerStyle,
    counterStyle,
    disabled,
    labelIcon,
    strokeColor,
    trackColor,
  }: Omit<CellProps, 'style'>) => {
    const { rpx } = useResponsiveContext();

    const progress = useSharedValue(0);

    useEffect(() => {
      progress.value = withTiming(value, { duration: 1000 });
    }, [progress, value]);

    return (
      <>
        <CircularProgress
          progress={progress}
          stroke={strokeColor}
          track={trackColor}
          strokeWidth={strokeWidth ?? rpx(3)}
          size={size ?? rpx(60)}
        >
          <Center style={StyleSheet.absoluteFill}>
            <Typography.Label
              bold
              size={((size ?? rpx(60)) * 1.2) / 4}
              lineHeight={size}
              style={counterStyle}
            >
              {count}
            </Typography.Label>
          </Center>
        </CircularProgress>
        {!noLabel && (
          <HStack justifyContent="center" style={labelContainerStyle}>
            {labelIcon}
            <Typography.Label
              regular
              size={rpx(10)}
              color={disabled ? kDisabledLabelColor : kLabelColor}
              css={`
                margin-top: ${rpx(4)}px;
                text-align: center;
              `}
              style={labelStyle}
            >
              {defectiveLevel === DefectiveLevel.Fine
                ? '正常状态'
                : getDefectiveLevelText(defectiveLevel)}
            </Typography.Label>
          </HStack>
        )}
      </>
    );
  },
);

const IconView = memo(
  ({
    defectiveLevel,
    size,
    color,
    style,
  }: {
    defectiveLevel: DefectiveLevel;
    size?: number;
    color?: string;
    style?: StyleProp<ViewStyle>;
  }) => {
    const Icon = DefectiveIconMap[defectiveLevel];
    return <Icon size={size} color={color} style={style} />;
  },
);
